"use client";

import { usePathname } from "next/navigation";
import type * as React from "react";

import { AppInsetBody } from "@/components/app-main-width";
import { AuthGate } from "@/components/auth-gate";
import { CapacityNoticeBanner } from "@/components/capacity-notice-banner";
import { AppSidebar } from "@/components/sidebar.app";
import { SiteHeader } from "@/components/site-header";
import { SITE_NOTICE_BANNER_HEIGHT } from "@/components/site-notice-banner";
import { StripeSandboxBanner } from "@/components/stripe-sandbox-banner";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { WelcomeCreditRevealProvider } from "@/components/welcome-credit-reveal";
import { useAuth } from "@/lib/auth";
import { useStripeConfig } from "@/lib/useStripeConfig";

const isBillingRoute = (pathname: string | null): boolean =>
  pathname === "/manage/billing" || (pathname?.startsWith("/manage/billing/") ?? false);

export const AppShell = ({ children }: { children: React.ReactNode }) => {
  const pathname = usePathname();
  const { capacityStatus } = useAuth();
  const { isTestMode, error: stripeConfigError } = useStripeConfig();

  const showCapacityNotice = capacityStatus != null && capacityStatus.state !== "granted";
  const showSandboxNotice = isBillingRoute(pathname) && !stripeConfigError && isTestMode;
  const noticeCount = (showCapacityNotice ? 1 : 0) + (showSandboxNotice ? 1 : 0);
  const noticeOffset =
    noticeCount === 0
      ? "0px"
      : noticeCount === 1
        ? SITE_NOTICE_BANNER_HEIGHT
        : `calc(${SITE_NOTICE_BANNER_HEIGHT} * ${noticeCount})`;

  return (
    <AuthGate>
      <WelcomeCreditRevealProvider>
        <SidebarProvider
          className="flex min-h-svh w-full flex-col"
          style={
            {
              "--sidebar-width": "calc(var(--spacing) * 72)",
              "--header-height": "calc(var(--spacing) * 12)",
              // Sidebar still reads this name; value is the sum of all stacked site notice strips.
              "--capacity-banner-offset": noticeOffset,
            } as React.CSSProperties
          }
        >
          <CapacityNoticeBanner variant="global" />
          {showSandboxNotice ? <StripeSandboxBanner /> : null}
          <div className="flex min-h-0 min-w-0 flex-1 flex-row">
            <AppSidebar variant="sidebar" />
            <SidebarInset>
              <SiteHeader />
              <AppInsetBody className="flex flex-1 flex-col">{children}</AppInsetBody>
            </SidebarInset>
          </div>
        </SidebarProvider>
      </WelcomeCreditRevealProvider>
    </AuthGate>
  );
};
